import React from "react";
import styled from "styled-components";
import Xarrow from "react-xarrows";
import { useXarrow } from "react-xarrows";
import {
    ActionButton,
    ActionsRender,
    XMappingDragItem,
    XMappingItemRender,
    XMappingListRender,
    XMappingSectionHeader,
} from "../Builder/CommonStyles";
import { MappingEditContext } from "../Builder/MappingEditContext";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { CollapseIcon } from "../../CommonUI/Icon/CollapseIcon";
import { ExpandGrayIcon } from "../../CommonUI/Icon/ExpandGrayIcon";
import { MappingConstants } from "../../rule-engine-core/Mapping/MappingConstants";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { DragConnectorRender } from "./DragConnector";
import { useDrag } from "react-dnd";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export interface IMappingSource {
    dataDefinition: DataDefinition;
    editContext: MappingEditContext;
}

export const XMappingSource = (props: IMappingSource) => {
    const updateXarrow = useXarrow();
    const ref = React.useRef<HTMLDivElement | null>(null);
    return (
        <XMappingSourceRender>
            <XMappingSectionHeader>Source</XMappingSectionHeader>
            <XMappingListRender
                ref={ref}
                onScroll={(e) => {
                    if (ref.current) {
                        ref.current.scrollLeft = 0;
                    }
                    if (e.target === ref.current) {
                        updateXarrow();
                    }
                }}
            >
                <ObserveState
                    listenTo={props.editContext}
                    control={() => {
                        return (
                            <>
                                {DataUtilities.getAllSubFieldsWithPath(props.dataDefinition).map((fieldWithPath) => {
                                    const { field, path } = fieldWithPath;
                                    let isCollapsed = props.editContext.isSourceCollapsed(path);
                                    return (
                                        <XMappingRuleItemColumn
                                            size={path.includes(".") ? "Small" : "Medium"}
                                            deepsCount={path.split(".").length - 1}
                                            key={path}
                                            isCollapsed={isCollapsed}
                                        >
                                            {isCollapsed ? (
                                                <div
                                                    id={props.editContext.sourceDragId(path)}
                                                    style={{ marginTop: -4, width: "100%" }}
                                                />
                                            ) : (
                                                <XMappingSourceItem
                                                    field={field}
                                                    path={path}
                                                    editContext={props.editContext}
                                                />
                                            )}
                                        </XMappingRuleItemColumn>
                                    );
                                })}
                            </>
                        );
                    }}
                />
            </XMappingListRender>
        </XMappingSourceRender>
    );
};

export interface IMappingSourceItem {
    field: IDataDefinition;
    editContext: MappingEditContext;
    path: string;
    isCollapsed?: boolean;
}

export const XMappingSourceItem = (props: IMappingSourceItem) => {
    const [{ isDragging }, drag] = useDrag({
        type: "field",
        item: { type: "field", path: props.path },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    let parentPath: string | undefined;

    if (props.path.includes(".")) {
        let pieces = props.path.split(".");
        parentPath = pieces.slice(0, -1).join(".");
    }

    let isCollapsed = props.editContext.isSourceCollapsed(props.path, false);

    let currentIdForDnd = props.editContext.sourceId(props.path);
    let itemSize: "Small" | "Medium" = props.path.includes(".") ? "Small" : "Medium";

    //let connectedRules = props.editContext.definition.getMappingRulesByInputPath(props.path);

    return (
        <XMappingItemRender
            id={currentIdForDnd}
            size={itemSize}
        >
            <XMappingDragItem
                ref={drag}
                size={itemSize}
                highlighted={isDragging}
                onMouseEnter={() => {
                    props.editContext.hoveredSource = props.path;
                }}
                onMouseLeave={() => {
                    props.editContext.hoveredSource = undefined;
                }}
            >
                {props.field.name}
            </XMappingDragItem>

            {props.field instanceof ObjectDataDefinition ? (
                <XMappingConnectorForChildren id={props.editContext.sourceConnectorIdForChildren(props.path)} />
            ) : parentPath ? (
                <>
                    <XMappingConnectorForParent id={props.editContext.sourceConnectorIdForParent(props.path)} />
                    <Xarrow
                        start={props.editContext.sourceConnectorIdForParent(props.path)}
                        end={props.editContext.sourceConnectorIdForChildren(parentPath)}
                        startAnchor="left"
                        endAnchor="bottom"
                        color={MappingConstants.MAPPING_LINE_COLOR}
                        strokeWidth={1}
                        showHead={false}
                        path="grid"
                    />
                </>
            ) : null}

            {props.field instanceof ObjectDataDefinition && (
                <ActionsRender className="ActionsRender">
                    <ActionButton
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            event.stopPropagation();
                            props.editContext.toggleSourceCollapse(props.path);
                        }}
                    >
                        {!isCollapsed ? <CollapseIcon /> : <ExpandGrayIcon />}
                    </ActionButton>
                </ActionsRender>
            )}

            <DragConnectorRender
                direction="right"
                id={props.editContext.sourceDragId(props.path)}
            />
        </XMappingItemRender>
    );
};

const XMappingSourceRender = styled.div`
    width: 300px;

    border-right: 1px dashed ${UIStyleConstants.BLUE_LIGHT_COLOR};
    display: flex;
    flex-direction: column;

    height: 100%;

    padding-bottom: 20px;
`;

const XMappingRuleItemColumn = styled.div<{ size: "Small" | "Medium"; deepsCount: number; isCollapsed?: boolean }>`
    display: flex;
    align-items: center;

    border-left: 1px dashed ${UIStyleConstants.BLUE_LIGHT_COLOR};

    position: sticky;
    left: 0;

    padding: ${(props) =>
        props.isCollapsed
            ? `0 ${(props.deepsCount - 1) * 8 + 24}px 0 ${(props.deepsCount - 1) * 25 + 56}px`
            : `${props.size === "Small" ? "2px" : "4px"} ${props.deepsCount * 8 + 24}px ${
                  props.size === "Small" ? "2px" : "4px"
              } ${props.deepsCount * 25 + 56}px`};
`;

const XMappingConnectorForChildren = styled.div`
    position: absolute;
    top: 100%;
    left: 8px;
`;

const XMappingConnectorForParent = styled.div`
    position: absolute;
    top: 50%;
    left: 0px;
`;
